import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { 
  FaSearch, 
  FaRocket, 
  FaMagic, 
  FaPuzzlePiece, 
  FaCheckCircle
} from 'react-icons/fa';
import './TailoringProcess.css';

const TailoringProcess = ({ status, progress, jobTitle, company }) => {
  const [activeStage, setActiveStage] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  
  // Define the stages of processing
  const stages = [
    {
      name: 'Analyzing',
      icon: <FaSearch />,
      description: 'Analyzing job description and requirements',
      details: 'Our AI is identifying key skills, qualifications, and responsibilities from the job description.'
    },
    {
      name: 'Matching',
      icon: <FaPuzzlePiece />,
      description: 'Matching your profile to job requirements',
      details: 'Finding the strongest connections between your experience and what the employer is looking for.'
    },
    {
      name: 'Optimizing',
      icon: <FaMagic />,
      description: 'Optimizing content and formatting',
      details: 'Refining language, ordering sections, and highlighting the most relevant achievements.'
    },
    {
      name: 'Finalizing',
      icon: <FaRocket />,
      description: 'Finalizing your tailored resume',
      details: 'Putting the finishing touches on your perfectly tailored resume.'
    },
    {
      name: 'Completed',
      icon: <FaCheckCircle />,
      description: 'Your tailored resume is ready!',
      details: 'Your resume has been tailored to match the job requirements and highlight your relevant skills.'
    }
  ];
  
  // Resume tips to display while processing
  const resumeTips = [
    "Use numbers and metrics to quantify your achievements when possible",
    "Tailor your resume for each job application to highlight relevant experience",
    "Keep your resume concise - typically 1-2 pages depending on experience level",
    "Use action verbs to begin bullet points describing your experience",
    "Ensure your contact information is up-to-date and professional",
    "Most recruiters spend less than 10 seconds scanning a resume initially",
    "Include keywords from the job description to pass automated screening systems",
    "Proofread carefully - typos and grammar errors can eliminate your candidacy",
    "Focus on achievements rather than just listing job duties",
    "Consider including a brief professional summary at the top of your resume"
  ];
  
  // Update active stage based on status
  useEffect(() => {
    let stageIndex = 0;
    
    switch (status) {
      case 'analyzing':
        stageIndex = 0;
        break;
      case 'matching':
        stageIndex = 1;
        break;
      case 'optimizing':
        stageIndex = 2;
        break;
      case 'finalizing':
        stageIndex = 3;
        break;
      case 'completed':
        stageIndex = 4;
        break;
      default:
        stageIndex = 0;
    }
    
    setActiveStage(stageIndex);
  }, [status]);
  
  // Rotate through tips every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex(prev => (prev + 1) % resumeTips.length);
    }, 8000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="tailoring-process">
      <div className="process-header">
        <h2>Tailoring Your Resume</h2>
        <p>
          {jobTitle && company 
            ? `We're tailoring your resume for the ${jobTitle} position at ${company}`
            : jobTitle
              ? `We're tailoring your resume for the ${jobTitle} position`
              : company
                ? `We're tailoring your resume for ${company}`
                : "We're tailoring your resume based on the job description"}
        </p>
      </div>
      
      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="progress-percentage">{progress}%</div>
      </div>
      
      <div className="process-visualization">
        <div className="stages-container">
          {stages.map((stage, index) => (
            <div 
              key={index}
              className={`
                process-stage 
                ${index === activeStage ? 'active' : ''} 
                ${index < activeStage ? 'completed' : ''}
              `}
            >
              <div className="stage-icon">
                {stage.icon}
              </div>
              <div className="stage-content">
                <h3 className="stage-name">{stage.name}</h3>
                <p className="stage-description">{stage.description}</p>
                {index === activeStage && (
                  <p className="stage-details">{stage.details}</p>
                )}
              </div>
              {index < stages.length - 1 && (
                <div className="stage-connector"></div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      <div className="resume-tips">
        <div className="tips-header">
          <h3>Resume Tips</h3>
          <div className="tip-indicator">
            {resumeTips.map((_, index) => (
              <span 
                key={index} 
                className={`indicator-dot ${index === tipIndex ? 'active' : ''}`}
              ></span>
            ))}
          </div>
        </div>
        <div className="tip-content">
          <p className="current-tip">{resumeTips[tipIndex]}</p>
        </div>
      </div>
      
      <div className="tailoring-note">
        <p>
          You'll be able to download and view your tailored resume once the process is complete.
          This typically takes less than a minute.
        </p>
      </div>
    </div>
  );
};

TailoringProcess.propTypes = {
  status: PropTypes.string.isRequired,
  progress: PropTypes.number.isRequired,
  jobTitle: PropTypes.string,
  company: PropTypes.string
};

export default TailoringProcess;